<script lang="ts">
import { ERoutes } from '../../../config/enums.js';
import { isContactOpen, isMenuOpen } from '../../../utils/store.js';
import Logo from '../../assets/logo.svg?raw';
import ContactMobileIcon from '../../assets/mobile-contact.svg?raw';
import Link from '../../atoms/Link/Link.svelte';
import Overlay from '../../atoms/Overlay/Overlay.svelte';
import MenuTrigger from '../../molecules/MenuTrigger/MenuTrigger.svelte';
import Menu from '../Menu/Menu.svelte';

interface Props {
    transparentHeader?: boolean;
    makeSticky?: boolean;
    altNavColour?: boolean;
    altButtonColour?: boolean;
}

let {
    transparentHeader = $bindable(true),
    makeSticky = $bindable(false),
    altNavColour = false,
    altButtonColour = false,
}: Props = $props();

const triggerY: number = 16;
const stickyTriggerY: number = 16; /* size of final sticky padding-top */
let scrollY: number = $state(0);

$effect(() => {
    if (transparentHeader && scrollY > triggerY) {
        transparentHeader = false;
    } else if (!transparentHeader && scrollY <= triggerY) {
        transparentHeader = true;
    }
    if (scrollY > stickyTriggerY) {
        makeSticky = true;
    } else {
        makeSticky = false;
    }
});

let useAltButtonColourClass = $state('');

$effect(() => {
    useAltButtonColourClass = altButtonColour || makeSticky ? 'button--inverse' : '';
});
</script>

<svelte:window bind:scrollY />

<Overlay showOverlay={$isContactOpen} />

<header
    class="header"
    class:header--transparent={transparentHeader}
    class:header--is-stuck={makeSticky}
    class:header--alt-nav-colour={altNavColour}
>
    <Menu />
    <MenuTrigger
        burgerColour={altNavColour || makeSticky
            ? 'var(--colour-dreamless-sleep)'
            : 'var(--colour-white)'}
    />
    <div class="logo" class:logo--transparent={transparentHeader}>
        <Link to="/" class="logo-link">
            {#snippet text()}
                {@html Logo}
            {/snippet}
        </Link>
    </div>

    <div class="button-container">
        <Link
            to={ERoutes.Contact}
            type="button"
            class="button {useAltButtonColourClass} get-in-touch"
            onClick={() => {
                $isMenuOpen = false;
                $isContactOpen = true;
            }}
        >
            {#snippet text()}
                Contact
                {@html ContactMobileIcon}
            {/snippet}
        </Link>
    </div>
</header>

<style lang="scss">
    @use '/src/scss/global-constants/typography';
    @use 'sass-mq/mq' as *;

    .header {
        view-transition-name: header;
        padding: 1.6rem var(--layout-pagegrid-margin);
        align-items: center;
        background: var(--colour-white);
        box-shadow: 0 0.5rem 1rem 0 rgba(0, 0, 0, 0.15);
        display: flex;
        justify-content: space-between;
        position: fixed;
        top: var(--header-top-offset, 0px);
        transition:
            background-color var(--timing-header-animation) ease-in,
            color var(--timing-header-animation) ease-in;
        width: 100%;
        will-change: background-color, color;
        z-index: 11;

        @include mq($from: desktop) {
            position: absolute;
            padding-top: 4rem;
            z-index: 10;
        }

        &--transparent {
            background-color: transparent;
            box-shadow: none;
        }

        &--is-stuck {
            @include mq($from: desktop) {
                position: sticky;
                padding-top: 1.6rem;
                margin-top: -63px; /* height of the final stuck header (not original absolute header) */
            }
        }
    }

    .logo {
        flex: 0 0 12.8rem;
        text-align: center;
        align-self: center;


        @include mq($from: 'tablet') {
            flex: 0 0 20rem;
        }
    }

    .header--is-stuck .logo {
        @include mq($from: 'tablet') {
            flex: 0 0 12.5rem;
        }
    }

    .logo :global(svg) {
        max-width: 100%;
    }

    .header--alt-nav-colour .logo :global(svg) {
        color: var(--colour-dreamless-sleep);
    }

    .header :global(.get-in-touch) {
        @include mq($until: 'tablet') {
            color: var(--colour-white);
            background: none;
            border: none;
            font-size: 0;
            line-height: 0;
            padding: 0;
            vertical-align: middle;
            border-radius: 0;

            &::after {
                display: none;
            }
        }

        @include mq($from: 'tablet') {
            display: inline;
        }
    }

    .header--is-stuck :global(.get-in-touch) {
        @include mq($until: 'tablet') {
            color: var(--colour-dreamless-sleep);
        }
    }

    .header--alt-nav-colour :global(.get-in-touch) {
        @include mq($until: 'tablet') {
            color: var(--colour-dreamless-sleep);
        }
    }

    .header :global(.get-in-touch svg) {
        display: block;

        @include mq($from: 'tablet') {
            display: none;
        }
    }

    .button-container {
        @include mq($until: 'tablet') {
            line-height: 0;
            opacity: 1;
            transition: opacity var(--timing-duration-short);
        }

        @include mq($from: 'tablet') {
            flex: 0 1 50%;
            text-align: right;
        }
    }

    .header :global(a) {
        color: var(--colour-white);
    }

    .header :global(.menu a) {
        color: var(--colour-dreamless-sleep);

        @include mq($from: 'tablet') {
            color: var(--colour-white);
        }
    }

    .header--alt-nav-colour :global(.menu a) {
        @include mq($from: 'tablet') {
            color: var(--colour-dreamless-sleep);
        }
    }

    .header--alt-nav-colour :global(a) {
        color: var(--colour-dreamless-sleep);
    }

    .header--is-stuck :global(a) {
        color: var(--colour-dreamless-sleep);
    }

    .header--is-stuck :global(.menu a) {
        @include mq($from: 'tablet') {
            color: var(--colour-dreamless-sleep);
        }
    }

    .header:has(:global(.menu--open)) :global(.get-in-touch),
    .header:has(:global(.menu--open)) :global(a) {
        color: var(--colour-dreamless-sleep);
    }
</style>
